import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import {
  Loader2, Settings2, Play, ChevronDown, ChevronRight,
  X, Activity, LayoutDashboard, Sparkles, AlertCircle, WrenchIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NativeSelect } from '../components/ui/native-select';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const today = () => new Date().toISOString().slice(0, 10);

function ConfigLabel({ children, label }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground/80">{label}</label>
      {children}
    </div>
  );
}

const LOGIC_COLORS = {
  'Trend Following': 'bg-primary/15 text-primary border-primary/20',
  'Mean Reversion':  'bg-purple-500/15 text-purple-500 border-purple-500/20',
  'Breakout':        'bg-orange-500/15 text-orange-500 border-orange-500/20',
  'Momentum':        'bg-emerald-500/15 text-emerald-500 border-emerald-500/20',
};

const DEFAULT_WEIGHTS = {
  feature_overlap:     0.20,
  parameter_proximity: 0.10,
  logic_type:          0.20,
  signal_correlation:  0.15,
  sector_bias:         0.05,
  equity_correlation:  0.15,
  risk_metrics:        0.15,
};

const WEIGHT_LABELS = {
  feature_overlap:     'Feature Overlap',
  parameter_proximity: 'Parameter Proximity',
  logic_type:          'Logic Type',
  signal_correlation:  'Signal Correlation',
  sector_bias:         'Sector Bias',
  equity_correlation:  'Equity Curve Corr',
  risk_metrics:        'Risk Metrics',
};

const DEFAULT_BT_CONFIG = {
  initial_funds:       100000,
  max_positions:       5,
  entry:               'next_open',
  stop_loss_pct:       '',
  take_profit_pct:     '',
  trailing_stop_pct:   '',
  max_holding_days:    '',
  atr_stop_multiplier: '',
  atr_stop_mode:       'fixed',
  atr_stop_period:     14,
  exit_slippage_pct:   '',
  position_sizing:     'equal_weight',
  position_size_value: '',
  vol_lookback:        14,
  vol_atr_multiplier:  2.0,
  start_date:          '2022-01-01',
  end_date:            today(),
};

const SIZING_OPTIONS = [
  { value: 'equal_weight', label: 'Equal weight' },
  { value: 'fixed_amount', label: 'Fixed amount (₹)' },
  { value: 'fixed_pct',    label: 'Fixed % of portfolio' },
  { value: 'volatility',   label: 'Volatility-based (ATR)' },
];

function pct(v)  { return v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`; }
function num(v)  { return v == null ? '—' : Number(v).toFixed(2); }
function round2(v) { return Math.round(v * 100) / 100; }

function corrColor(v) {
  if (v >= 0) {
    const g = Math.round(40 + v * 100);
    const r = Math.round(30 + (1 - v) * 20);
    return `rgb(${r},${g},${Math.round(30 + (1 - v) * 20)})`;
  } else {
    const r = Math.round(100 + (-v) * 100);
    return `rgb(${r},30,30)`;
  }
}

function compColor(v) {
  if (v >= 0.5) {
    const intensity = (v - 0.5) * 2;
    const g = Math.round(80 + intensity * 120);
    return `rgb(20,${g},40)`;
  } else {
    const intensity = (0.5 - v) * 2;
    const r = Math.round(80 + intensity * 120);
    return `rgb(${r},40,20)`;
  }
}

function weightsSum(w) {
  return round2(Object.values(w).reduce((a, b) => a + b, 0));
}

function LogicBadge({ logic }) {
  const cls = LOGIC_COLORS[logic] || 'bg-muted text-muted-foreground border-border';
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${cls}`}>{logic || 'Unknown'}</span>;
}

function MetricPill({ label, value }) {
  return (
    <div className="flex flex-col items-center justify-center p-2 bg-muted/40 rounded-md border border-border/50 min-w-[70px] flex-1">
      <span className="text-sm font-semibold text-foreground tracking-tight">{value}</span>
      <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-wider mt-0.5">{label}</span>
    </div>
  );
}

function HelmStatPill({ label, value, highlight }) {
  return (
    <div className={`flex flex-col items-center justify-center p-2 rounded-md border min-w-[70px] flex-1 transition-colors ${
      highlight ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-muted/40 border-border/50'
    }`}>
      <span className={`text-sm font-semibold tracking-tight ${highlight ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}>{value}</span>
      <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-wider mt-0.5">{label}</span>
    </div>
  );
}

function ClusterCard({ cluster, isHelm, onCreateHelm }) {
  const m = cluster.avg_metrics;
  return (
    <Card className={`group hover:shadow-md hover:border-primary/30 transition-all duration-200 hover:-translate-y-0.5 flex flex-col ${
      isHelm ? 'border-emerald-500/20' : 'border-border'
    }`}>
      <CardContent className="flex flex-col gap-4 p-5 h-full">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-foreground flex items-center gap-1.5">
                {isHelm ? <Sparkles className="w-4 h-4 text-emerald-500" /> : <LayoutDashboard className="w-4 h-4 text-primary" />}
                {isHelm ? 'Helm Group' : 'Cluster'} {cluster.cluster_id + 1}
              </span>
              <LogicBadge logic={cluster.dominant_logic} />
            </div>
            {isHelm && cluster.complementarity_score > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-medium inline-flex items-center w-fit">
                ⇄ {(cluster.complementarity_score * 100).toFixed(0)}% complement
              </span>
            )}
          </div>
          <span className="text-xs font-medium bg-muted px-2 py-1 rounded-md text-muted-foreground shrink-0">
            {cluster.screener_ids.length} screener{cluster.screener_ids.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Body: Screeners */}
        <div className='flex flex-col gap-2 grow'>
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Screeners</span>
          <div className="flex flex-wrap gap-1.5">
            {cluster.screener_names.map((name, i) => {
              const isRep = cluster.screener_ids[i] === cluster.representative_id;
              return (
                <span key={i} className={`text-xs px-2.5 py-1 rounded-md border font-medium shadow-xs transition-colors ${
                  isRep
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'bg-background border-border text-foreground hover:bg-muted'
                }`}>
                  {name}
                  {isRep && !isHelm && <span className="ml-1 opacity-70 text-[10px]">(Rep)</span>}
                </span>
              );
            })}
          </div>

          {cluster.common_attr_types?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {cluster.common_attr_types.map((a) => (
                <span key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground border border-border/50">
                  {a.replace('Attribute', '')}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer: Metrics */}
        <div className='mt-4 pt-4 border-t border-border/50'>
          {isHelm ? (
            <div className="flex gap-2 flex-wrap justify-between">
              <HelmStatPill label="Coverage"   value={`${((cluster.coverage_pct || 0) * 100).toFixed(0)}%`}   highlight={cluster.coverage_pct > 0.8} />
              <HelmStatPill label="DD Overlap" value={`${((cluster.drawdown_overlap_pct || 0) * 100).toFixed(0)}%`} highlight={cluster.drawdown_overlap_pct < 0.2} />
              <HelmStatPill label="Handoffs"   value={cluster.handoff_count ?? '—'} />
              <HelmStatPill label="Logic Div"  value={(cluster.logic_diversity || 0).toFixed(2)} highlight={cluster.logic_diversity > 0.7} />
              <MetricPill   label="CAGR"       value={pct(m.cagr)} />
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap justify-between">
              <MetricPill label="CAGR"     value={pct(m.cagr)} />
              <MetricPill label="Sharpe"   value={num(m.sharpe)} />
              <MetricPill label="Drawdown" value={pct(m.max_drawdown)} />
              <MetricPill label="Win Rate" value={pct(m.win_rate)} />
              <MetricPill label="Avg Hold" value={m.avg_holding_days != null ? `${Number(m.avg_holding_days).toFixed(0)}d` : '—'} />
            </div>
          )}
        </div>

        {isHelm && (
          <Button
            size="sm"
            variant="outline"
            className="w-full mt-1 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-600"
            onClick={() => onCreateHelm(cluster)}
          >
            <WrenchIcon className="w-3.5 h-3.5 mr-1.5" /> Create Helm from Group
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function PairwiseTable({ rows }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length === 0) return null;
  return (
    <Card className="overflow-hidden border-border bg-background shadow-xs">
      <button
        className="w-full flex items-center justify-between p-4 text-sm font-medium hover:bg-muted/30 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" /> 
          Pairwise Similarity Analysis
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>
      
      {open && (
      <div className="overflow-x-auto border-t border-border">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              {['Screener A', 'Screener B', 'Logic A', 'Logic B', 'Signal Jaccard', 'Attr Overlap', 'Equity Corr'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5 text-sm font-medium text-foreground">{r.screener_a_name}</td>
                <td className="px-4 py-2.5 text-sm font-medium text-foreground">{r.screener_b_name}</td>
                <td className="px-4 py-2.5"><LogicBadge logic={r.logic_a} /></td>
                <td className="px-4 py-2.5"><LogicBadge logic={r.logic_b} /></td>
                <td className="px-4 py-2.5 text-sm text-muted-foreground">{(r.signal_jaccard * 100).toFixed(1)}%</td>
                <td className="px-4 py-2.5 text-sm text-muted-foreground">{(r.attr_overlap * 100).toFixed(0)}%</td>
                <td className={`px-4 py-2.5 text-sm font-medium ${r.equity_corr > 0.5 ? 'text-emerald-600' : r.equity_corr < -0.2 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {r.equity_corr != null ? r.equity_corr.toFixed(3) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </Card>
  );
}

function EquityHeatmap({ matrix, names }) {
  if (!matrix || matrix.length === 0) return null;
  return (
    <div className="overflow-x-auto border border-border rounded-lg shadow-xs">
      <table className="text-[11px] border-collapse bg-background w-full">
        <thead>
          <tr className="bg-muted/50">
            <th className="sticky left-0 z-10 bg-muted/50 border-r border-border/50 w-24"></th>
            {names.map((n) => (
              <th key={n} className="px-2 py-2 text-muted-foreground font-medium text-center max-w-[80px] truncate border-b border-border/50" title={n}>
                {n}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, i) => (
            <tr key={i} className="hover:bg-muted/20">
              <td className="sticky left-0 z-10 bg-background border-r border-border/50 px-2 py-1.5 text-right font-medium text-muted-foreground max-w-[100px] truncate" title={names[i]}>
                {names[i]}
              </td>
              {row.map((val, j) => (
                <td
                  key={j}
                  style={{ background: i === j ? 'transparent' : corrColor(val) }}
                  title={`${names[i]} × ${names[j]}: ${val.toFixed(3)}`}
                  className={`w-12 h-8 text-center transition-opacity hover:opacity-80 cursor-default ${i !== j ? 'text-white/90 font-medium border border-black/5' : ''}`}
                >
                  {i === j ? '—' : val.toFixed(2)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComplementarityHeatmap({ matrix, names }) {
  if (!matrix || matrix.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 px-1">
        <Sparkles className="w-4 h-4 text-emerald-500" />
        <p className="text-sm text-muted-foreground">
          Pairwise complement score (0–1). <span className="text-emerald-500 font-medium">Green</span> = strong complement (one performs well when the other drops).
        </p>
      </div>
      <div className="overflow-x-auto border border-border rounded-lg shadow-xs">
        <table className="text-[11px] border-collapse bg-background w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="sticky left-0 z-10 bg-muted/50 border-r border-border/50 w-24"></th>
              {names.map((n) => (
                <th key={n} className="px-2 py-2 text-muted-foreground font-medium text-center max-w-[80px] truncate border-b border-border/50" title={n}>
                  {n}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, i) => (
              <tr key={i} className="hover:bg-muted/20">
                <td className="sticky left-0 z-10 bg-background border-r border-border/50 px-2 py-1.5 text-right font-medium text-muted-foreground max-w-[100px] truncate" title={names[i]}>
                  {names[i]}
                </td>
                {row.map((val, j) => (
                  <td
                    key={j}
                    style={{ background: i === j ? 'transparent' : compColor(val) }}
                    title={`${names[i]} × ${names[j]}: ${val.toFixed(3)}`}
                    className={`w-12 h-8 text-center transition-opacity hover:opacity-80 cursor-default ${i !== j ? 'text-white/90 font-medium border border-black/5' : ''}`}
                  >
                    {i === j ? '—' : val.toFixed(2)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeatureDetail({ features }) {
  const [open, setOpen] = useState(false);
  if (!features || features.length === 0) return null;
  return (
    <Card className="overflow-hidden border-border bg-background shadow-xs">
      <button
        className="w-full flex items-center justify-between p-4 text-sm font-medium hover:bg-muted/30 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-primary" /> 
          Per-Screener Feature Breakdown
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border-t border-border bg-muted/10">
          {features.map((f) => (
            <div key={f.screener_id} className="flex flex-col gap-3 p-4 bg-background rounded-lg border border-border shadow-xs">
              <div className="flex items-center justify-between gap-2 border-b border-border/50 pb-2">
                <span className="font-semibold text-foreground text-sm">{f.screener_name}</span>
                <LogicBadge logic={f.primary_logic} />
              </div>
              
              <div className="flex flex-col gap-2.5 text-xs">
                {[
                  { label: 'Indicators', val: (f.attr_types || []).map(a => (
                    <span key={a} className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground mr-1 mb-1 border border-border/50">{a.replace('Attribute', '')}</span>
                  ))},
                  { label: 'Period range', val: <span className="font-medium text-foreground">{f.period_range?.[0]}–{f.period_range?.[1]}</span> },
                  { label: 'Signal freq', val: <span className="font-medium text-foreground">{((f.signal_days_pct || 0) * 100).toFixed(1)}% of days</span> },
                  ...(f.has_backtest ? [{ label: 'Performance', val: <span className="font-medium text-foreground">CAGR {pct(f.performance?.cagr)} · Sharpe {num(f.performance?.sharpe)}</span> }] : []),
                ].map(({ label, val }) => (
                  <div key={label} className="flex gap-3">
                    <span className="text-muted-foreground font-medium w-24 shrink-0">{label}</span>
                    <span className="flex flex-wrap flex-1">{val}</span>
                  </div>
                ))}

                <div className="flex gap-3 mt-1">
                  <span className="text-muted-foreground font-medium w-24 shrink-0 mt-0.5">Logic scores</span>
                  <div className="flex gap-3 flex-wrap flex-1">
                    {Object.entries(f.logic_scores || {}).map(([k, v]) => (
                      <div key={k} className="flex flex-col items-center gap-1 group" title={`${k}: ${(v*100).toFixed(0)}%`}>
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase">{k.slice(0,2)}</span>
                        <div className="w-8 h-2 bg-muted rounded-full overflow-hidden border border-border/50">
                          <div className="h-full bg-primary/70 rounded-full transition-all group-hover:bg-primary" style={{ width: `${v*100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {Object.keys(f.sector_distribution || {}).some(k => f.sector_distribution[k] > 0) && (
                  <div className="flex gap-3 mt-1">
                    <span className="text-muted-foreground font-medium w-24 shrink-0 mt-0.5">Sector bias</span>
                    <div className="flex flex-wrap gap-1.5 flex-1">
                      {Object.entries(f.sector_distribution || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                        <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground border border-border/50">
                          {k} <span className="font-semibold text-foreground ml-0.5">{(v * 100).toFixed(0)}%</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


export default function Clustering() {
  const navigate = useNavigate();
  const [screeners, setScreeners]       = useState([]);
  const [selected, setSelected]         = useState(new Set());
  const [algorithm, setAlgorithm]       = useState('hierarchical');
  const [nClusters, setNClusters]       = useState('');
  const [inclBacktest, setInclBacktest] = useState(true);
  const [btConfig, setBtConfig]       = useState(DEFAULT_BT_CONFIG);
  const [weights, setWeights]           = useState({ ...DEFAULT_WEIGHTS });
  const [showWeights, setShowWeights]   = useState(false);

  const [objective, setObjective] = useState('complementarity');

  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');
  
  // Mobile config state
  const [isMobileConfigOpen, setIsMobileConfigOpen] = useState(false);

  // Create-helm-from-cluster modal state
  const [helmCluster, setHelmCluster]     = useState(null);
  const [helmName, setHelmName]           = useState('');
  const [helmDescription, setHelmDescription] = useState('');
  const [creatingHelm, setCreatingHelm]   = useState(false);
  const [helmError, setHelmError]         = useState('');

  useEffect(() => {
    // Open on load if mobile (optional, but false is usually better for initial view)
    api.get('/screeners').then((r) => {
      setScreeners(r.data);
      // setSelected(new Set(r.data.map((s) => s.id)));
    });
  }, []);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const setBt = (k, v) => setBtConfig((c) => ({ ...c, [k]: v }));
  const selectAll   = () => setSelected(new Set(screeners.map((s) => s.id)));
  const selectNone  = () => setSelected(new Set());

  const handleWeight = (key, raw) => {
    const val = Math.min(1, Math.max(0, parseFloat(raw) || 0));
    setWeights((prev) => ({ ...prev, [key]: round2(val) }));
  };

  const wSum = weightsSum(weights);
  const isHelm = objective === 'complementarity';

  const handleRun = async () => {
    if (selected.size < 2) { setError('Select at least 2 screeners'); return; }
    if (!isHelm && Math.abs(wSum - 1.0) > 0.05) { setError(`Weights must sum to 1.0 (currently ${wSum})`); return; }
    
    setError('');
    setResult(null);
    setRunning(true);
    setIsMobileConfigOpen(false); // Close mobile sidebar on run
    
    try {
      const body = {
        screener_ids:     [...selected],
        objective,
        algorithm,
        include_backtest: inclBacktest,
        ...(isHelm ? {} : { weights }),
      };
      if (nClusters) body.n_clusters = parseInt(nClusters, 10);
      if (inclBacktest) { 
        body.backtest_start = btConfig.start_date; body.backtest_end = btConfig.end_date; 
        
        body.initial_funds=       Number(btConfig.initial_funds);
        body.max_positions=       Number(btConfig.max_positions);
        body.stop_loss_pct=       btConfig.stop_loss_pct       ? Number(btConfig.stop_loss_pct)       : null;
        body.take_profit_pct=     btConfig.take_profit_pct     ? Number(btConfig.take_profit_pct)     : null;
        body.trailing_stop_pct=   btConfig.trailing_stop_pct   ? Number(btConfig.trailing_stop_pct)   : null;
        body.max_holding_days=    btConfig.max_holding_days     ? Number(btConfig.max_holding_days)    : null;
        body.atr_stop_multiplier= btConfig.atr_stop_multiplier  ? Number(btConfig.atr_stop_multiplier) : null;
        body.atr_stop_mode=       btConfig.atr_stop_mode || 'fixed';
        body.atr_stop_period=     Number(btConfig.atr_stop_period) || 14;
        body.exit_slippage_pct=   btConfig.exit_slippage_pct    ? Number(btConfig.exit_slippage_pct)   : 0;
        body.position_size_value= btConfig.position_size_value  ? Number(btConfig.position_size_value) : 0;
        body.vol_lookback=      Number(btConfig.vol_lookback);
        body.vol_atr_multiplier=  Number(btConfig.vol_atr_multiplier);
      }
      const res = await api.post('/cluster/screeners', body);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Clustering failed');
    } finally {
      setRunning(false);
    }
  };

  const openHelmDialog = (cluster) => {
    setHelmCluster(cluster);
    setHelmName(`Helm Group ${cluster.cluster_id + 1}`);
    setHelmDescription(cluster.description || '');
    setHelmError('');
  };

  const handleCreateHelm = async () => {
    if (!helmCluster) return;
    if (!helmName.trim()) { setHelmError('Name is required'); return; }

    setCreatingHelm(true);
    setHelmError('');
    try {
      const trade_config = inclBacktest ? {
        initial_funds:       Number(btConfig.initial_funds),
        max_positions:       Number(btConfig.max_positions),
        entry:               btConfig.entry,
        stop_loss_pct:       btConfig.stop_loss_pct       ? Number(btConfig.stop_loss_pct)       : null,
        take_profit_pct:     btConfig.take_profit_pct     ? Number(btConfig.take_profit_pct)     : null,
        trailing_stop_pct:   btConfig.trailing_stop_pct   ? Number(btConfig.trailing_stop_pct)   : null,
        max_holding_days:    btConfig.max_holding_days    ? Number(btConfig.max_holding_days)    : null,
        atr_stop_multiplier: btConfig.atr_stop_multiplier ? Number(btConfig.atr_stop_multiplier) : null,
        atr_stop_mode:       btConfig.atr_stop_mode || 'fixed',
        atr_stop_period:     Number(btConfig.atr_stop_period) || 14,
        position_sizing:     btConfig.position_sizing,
        position_size_value: btConfig.position_size_value ? Number(btConfig.position_size_value) : 0,
        vol_lookback:        Number(btConfig.vol_lookback),
        vol_atr_multiplier:  Number(btConfig.vol_atr_multiplier),
      } : undefined;

      const res = await api.post('/helms/from-cluster', {
        name:              helmName.trim(),
        description:       helmDescription.trim() || null,
        screener_ids:      helmCluster.screener_ids,
        trade_config_name: 'From Clustering',
        trade_config,
      });
      setHelmCluster(null);
      navigate(`/helms/${res.data.id}`, { state: { is_owner: true, is_global: false } });
    } catch (err) {
      setHelmError(err.response?.data?.error || 'Failed to create helm');
    } finally {
      setCreatingHelm(false);
    }
  };

  const selectedFeatures = result?.per_screener_features || [];
  const heatmapNames     = selectedFeatures.map((f) => f.screener_name);

  return (
    <AppLayout>
      <div className="flex flex-col md:flex-row h-[calc(100vh-4rem)] overflow-hidden bg-background">
        
        {/* Mobile Header Toggle */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-border bg-background z-10 shadow-xs">
          <div className="flex flex-col">
            <h1 className="text-lg font-bold text-foreground">Clustering</h1>
            <span className="text-xs text-muted-foreground">{selected.size} screeners selected</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setIsMobileConfigOpen(true)}>
            <Settings2 className="w-4 h-4 mr-2" /> Settings
          </Button>
        </div>

        {/* Mobile Backdrop */}
        {isMobileConfigOpen && (
          <div 
            className="fixed inset-0 bg-black/40 z-40 md:hidden transition-opacity"
            onClick={() => setIsMobileConfigOpen(false)}
          />
        )}

        {/* Sidebar Configuration */}
        <aside className={`
          fixed md:static inset-y-0 left-0 z-50 w-[85%] max-w-[350px] md:w-100 
          bg-background md:bg-muted/10 border-r border-border 
          flex flex-col transform transition-transform duration-300 ease-in-out
          ${isMobileConfigOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full md:translate-x-0'}
        `}>
          <div className="flex items-center justify-between p-4 border-b border-border md:hidden bg-background">
            <span className="font-bold text-foreground">Configuration</span>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileConfigOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6 scrollbar-thin">
            
            {/* Screeners Selection */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Screeners</h4>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs font-medium text-primary hover:text-primary/80 transition-colors">All</button>
                  <span className="text-muted-foreground/30">|</span>
                  <button onClick={selectNone} className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">None</button>
                </div>
              </div>
              <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto p-1 bg-background border border-border/50 rounded-md">
                {screeners.map((s) => (
                  <label key={s.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-sm hover:bg-muted cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                      className="rounded border-border text-primary focus:ring-primary/20"
                    />
                    <span className="text-sm font-medium text-foreground">{s.name}</span>
                  </label>
                ))}
                {screeners.length === 0 && <p className="text-xs text-muted-foreground p-2 text-center">Loading screeners...</p>}
              </div>
            </div>

            <div className="h-px bg-border/50 w-full" />

            {/* Model Setup */}
            <div className="flex flex-col gap-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Model Setup</h4>
              
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Objective</label>
                <NativeSelect value={objective} onChange={(e) => setObjective(e.target.value)} className="bg-background">
                  <option value="similarity">Similarity (Group alike)</option>
                  <option value="complementarity">Helm Complement (Group diverse)</option>
                </NativeSelect>
              </div>

              {!isHelm && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">Algorithm</label>
                  <NativeSelect value={algorithm} onChange={(e) => setAlgorithm(e.target.value)} className="bg-background">
                    <option value="hierarchical">Hierarchical (Complete linkage)</option>
                    <option value="kmeans">K-Means</option>
                  </NativeSelect>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Number of Clusters</label>
                <Input
                  type="number" min="2" max="10"
                  value={nClusters}
                  placeholder="Auto-determine"
                  onChange={(e) => setNClusters(e.target.value)}
                  className="bg-background h-9 text-sm"
                />
              </div>
            </div>

            <div className="h-px bg-border/50 w-full" />

            {/* Backtest Config */}
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={inclBacktest} 
                  onChange={(e) => setInclBacktest(e.target.checked)} 
                  className="rounded border-border text-primary focus:ring-primary/20" 
                />
                <span className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">Include Backtest Data</span>
              </label>
              
              {inclBacktest && (
                <>
                  <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1 flex justify-between items-baseline">
                    Exit <span className="text-xs font-normal text-muted-foreground">(blank = hold)</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <ConfigLabel label="Stop loss %">
                      <Input type="number" value={btConfig.stop_loss_pct} min="0" step="0.5" placeholder="e.g. 5" className="h-9" onChange={(e) => setBt('stop_loss_pct', e.target.value)} />
                    </ConfigLabel>
                    <ConfigLabel label="Take profit %">
                      <Input type="number" value={btConfig.take_profit_pct} min="0" step="0.5" placeholder="e.g. 15" className="h-9" onChange={(e) => setBt('take_profit_pct', e.target.value)} />
                    </ConfigLabel>
                    <ConfigLabel label="Trail stop %">
                      <Input type="number" value={btConfig.trailing_stop_pct} min="0" step="0.5" placeholder="e.g. 8" className="h-9" onChange={(e) => setBt('trailing_stop_pct', e.target.value)} />
                    </ConfigLabel>
                    <ConfigLabel label="Max days">
                      <Input type="number" value={btConfig.max_holding_days} min="1" placeholder="e.g. 20" className="h-9" onChange={(e) => setBt('max_holding_days', e.target.value)} />
                    </ConfigLabel>
                  </div>
                  <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                    <div className="col-span-2 text-xs font-medium text-muted-foreground">ATR stop (blank = off)</div>
                    <ConfigLabel label="ATR multiplier">
                      <Input type="number" value={btConfig.atr_stop_multiplier} min="0" step="0.1" placeholder="e.g. 2.5" className="h-9" onChange={(e) => setBt('atr_stop_multiplier', e.target.value)} />
                    </ConfigLabel>
                    {btConfig.atr_stop_multiplier != null && btConfig.atr_stop_multiplier !== '' && <ConfigLabel label="ATR period">
                      <Input type="number" value={btConfig.atr_stop_period} min="2" max="100" className="h-9" onChange={(e) => setBt('atr_stop_period', e.target.value)} />
                    </ConfigLabel>}
                    {btConfig.atr_stop_multiplier != null && btConfig.atr_stop_multiplier !== '' && <div className="col-span-2">
                      <ConfigLabel label="Mode">
                        <NativeSelect value={btConfig.atr_stop_mode} onChange={(e) => setBt('atr_stop_mode', e.target.value)} className="h-9 py-1.5">
                          <option value="fixed">Fixed (from entry)</option>
                          <option value="trailing">Trailing (from peak)</option>
                        </NativeSelect>
                      </ConfigLabel>
                    </div>}
                  </div>
                  <div className="flex flex-col gap-4">
                    <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1">Position sizing</h4>
                    <ConfigLabel label="Method">
                      <NativeSelect value={btConfig.position_sizing} onChange={(e) => setBt('position_sizing', e.target.value)} className="h-9 py-1.5">
                        {SIZING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </NativeSelect>
                    </ConfigLabel>
                    
                    {btConfig.position_sizing === 'fixed_amount' && (
                      <ConfigLabel label="Amount (₹)">
                        <Input type="number" value={btConfig.position_size_value} min="0" className="h-9" onChange={(e) => setBt('position_size_value', e.target.value)} />
                      </ConfigLabel>
                    )}
                    {btConfig.position_sizing === 'fixed_pct' && (
                      <ConfigLabel label="Percent (%)">
                        <Input type="number" value={btConfig.position_size_value} min="0" max="100" className="h-9" onChange={(e) => setBt('position_size_value', e.target.value)} />
                      </ConfigLabel>
                    )}
                    {btConfig.position_sizing === 'volatility' && (
                      <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                        <div className="col-span-2">
                          <ConfigLabel label="Risk per trade (%)">
                            <Input type="number" value={btConfig.position_size_value} min="0.1" max="100" step="0.1" placeholder="e.g. 1.0" className="h-9" onChange={(e) => setBt('position_size_value', e.target.value)} />
                          </ConfigLabel>
                        </div>
                        <ConfigLabel label="ATR period">
                          <Input type="number" value={btConfig.vol_lookback} min="2" max="100" className="h-9" onChange={(e) => setBt('vol_lookback', e.target.value)} />
                        </ConfigLabel>
                        <ConfigLabel label="Multiplier">
                          <Input type="number" value={btConfig.vol_atr_multiplier} min="0.1" step="0.1" className="h-9" onChange={(e) => setBt('vol_atr_multiplier', e.target.value)} />
                        </ConfigLabel>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-muted-foreground">Start Date</label>
                      <Input type="date" value={btConfig.start_date} onChange={(e) => setBt('start_date', e.target.value)} className="h-8 text-xs bg-background" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-muted-foreground">End Date</label>
                      <Input type="date" value={btConfig.end_date} onChange={(e) => setBt('end_date', e.target.value)} className="h-8 text-xs bg-background" />
                    </div>
                  </div>
                </>
              )}
            </div>

            {!isHelm && (
              <>
                <div className="h-px bg-border/50 w-full" />
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <button
                      className="flex items-center gap-1.5 text-sm font-bold text-foreground hover:text-primary transition-colors"
                      onClick={() => setShowWeights((o) => !o)}
                    >
                      {showWeights ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      Dimension Weights
                    </button>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${Math.abs(wSum - 1) > 0.05 ? 'bg-red-500/10 text-red-500' : 'bg-muted text-muted-foreground'}`}>
                      Σ = {wSum}
                    </span>
                  </div>
                  
                  {showWeights && (
                    <div className="flex flex-col gap-4 pt-2">
                      {Object.entries(WEIGHT_LABELS).map(([key, label]) => (
                        <div key={key} className="flex flex-col gap-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-medium text-muted-foreground uppercase">{label}</span>
                            <span className="text-xs font-semibold text-foreground">{(weights[key] * 100).toFixed(0)}%</span>
                          </div>
                          <input
                            type="range" min="0" max="0.5" step="0.01"
                            value={weights[key]}
                            onChange={(e) => handleWeight(key, e.target.value)}
                            className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                        </div>
                      ))}
                      <button 
                        className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors text-left mt-2 flex items-center gap-1" 
                        onClick={() => setWeights({ ...DEFAULT_WEIGHTS })}
                      >
                        Reset to defaults
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <span className="text-xs text-red-500 font-medium">{error}</span>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-border bg-background mt-auto">
            <Button 
              onClick={handleRun} 
              disabled={running || selected.size < 2} 
              className="w-full shadow-xs hover:shadow-md transition-all"
              size="lg"
            >
              {running ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
              ) : (
                <><Play className="h-4 w-4 mr-2" /> Run Clustering</>
              )}
            </Button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-muted/5 scrollbar-thin">
          {!result && !running && (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto opacity-70">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <LayoutDashboard className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">Ready to Cluster</h2>
              <p className="text-sm text-muted-foreground">
                Select your screeners from the sidebar, configure the model, and hit run to generate strategy clusters.
              </p>
            </div>
          )}

          {running && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
              <h2 className="text-lg font-semibold text-foreground mb-1">Analyzing Screeners...</h2>
              <p className="text-sm text-muted-foreground">Running complex mathematical models{inclBacktest ? ' and calculating backtests' : ''}.</p>
            </div>
          )}

          {result && !running && (
            <div className="flex flex-col gap-8 max-w-7xl mx-auto pb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
              
              {/* Results Header */}
              <div className="flex flex-col gap-3 border-b border-border/50 pb-6">
                <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  {result.objective === 'complementarity' ? <Sparkles className="w-6 h-6 text-emerald-500" /> : <LayoutDashboard className="w-6 h-6 text-primary" />}
                  {result.objective === 'complementarity' ? 'Helm Complement Groups' : 'Screener Clusters'}
                </h2>
                
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 bg-background border border-border px-3 py-1.5 rounded-full text-sm font-medium">
                    <span className="text-muted-foreground">{result.n_screeners} Screeners</span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
                    <span className="text-primary">{result.n_clusters} Groups</span>
                  </div>

                  {result.objective === 'complementarity' ? (
                    <span className="px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-sm font-semibold">
                      Complementarity Mode
                    </span>
                  ) : (
                    <span className="px-3 py-1.5 rounded-full bg-muted border border-border text-sm font-medium text-muted-foreground">
                      Algorithm: <strong className="text-foreground ml-1 capitalize">{result.algorithm}</strong>
                    </span>
                  )}
                  
                  {result.silhouette_score != null && (
                    <span className="px-3 py-1.5 rounded-full bg-muted border border-border text-sm font-medium text-muted-foreground">
                      Silhouette Score: <strong className="text-foreground ml-1">{result.silhouette_score.toFixed(3)}</strong>
                    </span>
                  )}
                </div>
              </div>

              {/* Cluster Cards Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {result.clusters.map((c) => (
                  <ClusterCard
                    key={c.cluster_id}
                    cluster={c}
                    isHelm={result.objective === 'complementarity'}
                    onCreateHelm={openHelmDialog}
                  />
                ))}
              </div>

              {/* Complementarity Heatmap */}
              {result.objective === 'complementarity' && result.complementarity_matrix?.length > 0 && (
                <div className="flex flex-col gap-4 mt-4">
                  <h3 className="text-lg font-bold text-foreground">Complementarity Matrix</h3>
                  <ComplementarityHeatmap matrix={result.complementarity_matrix} names={heatmapNames} />
                </div>
              )}

              {/* Equity Correlation Heatmap */}
              {result.objective !== 'complementarity' && inclBacktest && result.equity_correlations?.length > 0 && (
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-lg font-bold text-foreground">Equity Curve Correlation</h3>
                    <p className="text-sm text-muted-foreground">Pearson correlation of daily returns. Green indicates strong co-movement.</p>
                  </div>
                  <EquityHeatmap matrix={result.equity_correlations} names={heatmapNames} />
                </div>
              )}

              {/* Pairwise Table */}
              {result.pairwise_similarity?.length > 0 && (
                <div className="mt-4">
                  <PairwiseTable rows={result.pairwise_similarity} />
                </div>
              )}

              {/* Per-Screener Feature Breakdown */}
              <div className="mt-2">
                <FeatureDetail features={result.per_screener_features} />
              </div>

            </div>
          )}
        </main>
      </div>

      <Dialog open={!!helmCluster} onOpenChange={(open) => !open && setHelmCluster(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Helm from Group</DialogTitle>
            <DialogDescription>
              {helmCluster && `Bundles ${helmCluster.screener_ids.length} screener${helmCluster.screener_ids.length !== 1 ? 's' : ''} into a new helm, using the backtest config from the sidebar as its trade config.`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <ConfigLabel label="Name">
              <Input
                value={helmName}
                onChange={(e) => setHelmName(e.target.value)}
                placeholder="Helm name"
                className="bg-background"
              />
            </ConfigLabel>
            <ConfigLabel label="Description (optional)">
              <Input
                value={helmDescription}
                onChange={(e) => setHelmDescription(e.target.value)}
                placeholder="What this helm is for"
                className="bg-background"
              />
            </ConfigLabel>

            {helmCluster && (
              <div className="flex flex-wrap gap-1.5">
                {helmCluster.screener_names.map((n, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded-md border border-border bg-muted/40 text-foreground">{n}</span>
                ))}
              </div>
            )}

            {helmError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <span className="text-xs text-red-500 font-medium">{helmError}</span>
              </div>
            )}

            <Button onClick={handleCreateHelm} disabled={creatingHelm} className="w-full">
              {creatingHelm ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating...</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Create Helm</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}